import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import equal from 'fast-deep-equal/react';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../../models/Logging';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useInput } from '../../../../../models/hooks/inputHooks';
import { useSaveSavedSearchMutation } from '../../../../../queries/ContactSearch';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextInput } from '../../../../components/TextInput';
import { baseStyleSheet } from '../../../../styles/styles';
import { DefaultPeopleFilter } from '../PeopleContext';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	impersonationContext?: Api.IImpersonationContext;
}

export const SaveSearch: React.FC<IProps> = ({ className, styles, filter, impersonationContext }) => {
	const { pushApiError } = useErrorMessages();
	const [searchInput, setSearch, setSearchInput] = useInput('');
	const { logApiError } = useEventLogging();

	const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] = React.useState(false);

	// Use the save mutation hook
	const saveMutation = useSaveSavedSearchMutation({
		onSuccess: data => {
			toast.success(`Search "${data.name}" was saved.`);
			setIsSavedSearchModalOpen(false);
		},
		onError: (err: Api.IOperationResultNoValue) => {
			logApiError('SaveSearch-Error', err);
			pushApiError(err);
		},
	});

	const handleRequestClose = (result: string | undefined, isCancelled: boolean) => {
		setSearch('');
		if (isCancelled) {
			setIsSavedSearchModalOpen(false);
			return;
		}

		if (result) {
			saveMutation.mutate({
				name: result,
				filter: filter.filter,
				ownershipFilter: filter.ownershipFilter,
				impersonationContext,
			});
		}
	};

	const isNotDefaultFilter = React.useCallback(() => !equal(DefaultPeopleFilter, filter), [filter]);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			{isNotDefaultFilter() && (
				<a
					href='#'
					className={css(baseStyleSheet.brandLink, styleSheet.link)}
					onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
						e.preventDefault();
						setIsSavedSearchModalOpen(true);
					}}
				>
					Save Search
				</a>
			)}
			<Modal isOpen={isSavedSearchModalOpen} onRequestClose={handleRequestClose} useDefaultHeader={true}>
				<div className={css(styleSheet.saveSearchTitle)}>Save Search</div>
				<div className={css(styleSheet.saveSearchDescription)}>Name this search</div>
				<TextInput
					value={searchInput}
					onChange={setSearchInput}
					inputId='save-search-input'
					type='text'
					placeholder='Name this search'
				/>
				<div className={css(styleSheet.saveSearchButtonContainer)}>
					<Button
						label='Save'
						kind='primary'
						disabled={!searchInput || saveMutation.isPending}
						onClick={() => handleRequestClose(searchInput, false)}
					/>
					<Button label='Cancel' kind='reverse' onClick={() => handleRequestClose(undefined, true)} />
				</div>
			</Modal>
		</div>
	);
};
